import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useSeller } from '../contexts/SellerContext';
import PageHeader from '../components/PageHeader';
import {useLocation } from 'react-router-dom';
import {
  getCategoriesForSegment,
  getSubcategoriesForSegment,
} from './SellerSegment';
import AssortDrawer from '../components/AssortDrawer';
import Button from '../ui/components/Button';
import './Assortment.css';

const Assortment = () => {
  const { seller } = useSeller();
  const sellerId = seller?.sellerId;
  const location = useLocation();
  const serviceType = location.state?.serviceType;
  const segment = (seller?.segment)?.toUpperCase();

  const categories = useMemo(
    () => (segment ? getCategoriesForSegment(segment) : []),
    [segment]
  );

  const sourcesByCategory = useMemo(() => {
    const result = {};
    categories.forEach((category) => {
      result[category] = getSubcategoriesForSegment(segment, category);
    });
    return result;
  }, [segment, categories]);

  const [selectedCategory, setSelectedCategory] = useState(() => categories[0] || null);
  const [selectedSource, setSelectedSource] = useState(() => {
    const firstCategory = categories[0];
    return firstCategory ? (sourcesByCategory[firstCategory]?.[0] ?? '') : '';
  });

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  const [assortDrawerOpen, setAssortDrawerOpen] = useState(false);
  const [assortOrder, setAssortOrder] = useState(null);
  const [formData, setFormData] = useState({
    stampCertificateQtyPerSet: {},
    stampCertificateCost: '',
  });
  const categoryCounts = useMemo(() => {
    const counts = {};
    orders.forEach((order) => {
      const cat = order.category?.trim();
      if (cat) {
        counts[cat] = (counts[cat] || 0) + 1;
      }
    });
    return counts;
  }, [orders]);

  const subcategoryCounts = useMemo(() => {
    const counts = {};
    orders.forEach((order) => {
      const orderCat = order.category?.trim();
      const selectedCat = selectedCategory?.trim();
      if (!selectedCat || orderCat === selectedCat) {
        const sub = order.productSource?.trim();
        if (sub) {
          counts[sub] = (counts[sub] || 0) + 1;
        }
      }
    });
    return counts;
  }, [orders, selectedCategory]);

  useEffect(() => {
    if (!selectedCategory && categories.length > 0) {
      setSelectedCategory(categories[0]);
    }
  }, [categories, selectedCategory]);

  useEffect(() => {
    if (selectedCategory && (!selectedSource || !sourcesByCategory[selectedCategory]?.includes(selectedSource))) {
      setSelectedSource(sourcesByCategory[selectedCategory]?.[0] ?? '');
    }
  }, [selectedCategory, selectedSource, sourcesByCategory]);

  useEffect(() => {
    if (!hasAutoSelected && orders.length > 0 && categories.length > 0) {
      if (!selectedCategory || !categoryCounts[selectedCategory]) {
        const firstCatWithItems = categories.find((c) => categoryCounts[c] > 0);
        if (firstCatWithItems) {
          setSelectedCategory(firstCatWithItems);
          const subs = sourcesByCategory[firstCatWithItems] || [];
          const firstSubWithItems = subs.find((s) =>
            orders.some(
              (o) =>
                o.category?.trim() === firstCatWithItems.trim() &&
                o.productSource?.trim() === s.trim()
            )
          );
          setSelectedSource(firstSubWithItems || subs[0] || '');
          setHasAutoSelected(true);
        }
      }
    }
  }, [orders, categories, selectedCategory, categoryCounts, sourcesByCategory, hasAutoSelected]);

  const fetchOrders = useCallback(async () => {
    if (!sellerId) return;
    try {
      setLoading(true);
      setError(null);
      const constraints = [
        where('sellerId', '==', sellerId),
        where('orderStatus', 'in', ['Assortment']),
      ];
      if (serviceType) {
        constraints.push(where('serviceType', '==', serviceType));
      }
      const q = query(
        collection(db, 'orderList'),
        ...constraints
      );
      const snapshot = await getDocs(q);

      const fetchedOrders = [];
      snapshot.forEach((docSnap) => {
        fetchedOrders.push({
          firestoreId: docSnap.id,
          ...docSnap.data(),
        });
      });
      console.log(fetchedOrders);
      setOrders(fetchedOrders);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load requests.');
    } finally {
      setLoading(false);
    }
  }, [sellerId, serviceType]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders, refreshTrigger]);

  const handleFormChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleAssortClick = useCallback((order) => {
    setAssortOrder(order);
    setFormData({ stampCertificateQtyPerSet: {}, stampCertificateCost: '' });
    setAssortDrawerOpen(true);
  }, []);

  const handleConfirmAssort = useCallback(async () => {
    if (!assortOrder) return;

    setPendingAction('assort');

    try {
      const orderRef = doc(db, 'orderList', assortOrder.firestoreId);
      const orderSnap = await getDoc(orderRef);

      if (!orderSnap.exists()) {
        alert('Order not found. It may have been processed or deleted.');
        setOrders((prev) => prev.filter((o) => o.firestoreId !== assortOrder.firestoreId));
        return;
      }

      const data = orderSnap.data();
      if (data.sellerId !== sellerId) {
        alert('Not authorized to accept this order.');
        return;
      }

      const selectedVariants = assortOrder.selectedVariants?.map((variant) => ({
        ...variant,
        stampCertificateQtyPerSet: Number(formData.stampCertificateQtyPerSet?.[variant.size]) || 0,
      })) || [];

      await updateDoc(orderRef, {
        orderStatus: 'Assorted',
        selectedVariants,
        stampCertificateCost: Number(formData.stampCertificateCost) || 0,
        updatedAt: new Date(),
      });

      setRefreshTrigger((prev) => prev + 1);
      alert('Moved from Assortment.');
      setAssortDrawerOpen(false);
    } catch (err) {
      console.error('Error accepting order:', err);
      alert(`Failed to accept order: ${err.message}`);
    } finally {
      setPendingAction(null);
    }
  }, [assortOrder, sellerId, formData]);

  const handleRequestOperator = useCallback(async () => {
    if (!assortOrder) return;

    setPendingAction('requestOperator');

    try {
      const orderRef = doc(db, 'orderList', assortOrder.firestoreId);
      const orderSnap = await getDoc(orderRef);

      if (!orderSnap.exists()) {
        alert('Order not found.');
        return;
      }

      const data = orderSnap.data();
      if (data.sellerId !== sellerId) {
        alert('Not authorized.');
        return;
      }

      const selectedVariants = assortOrder.selectedVariants?.map((variant) => ({
        ...variant,
        stampCertificateQtyPerSet: Number(formData.stampCertificateQtyPerSet?.[variant.size]) || 0,
      })) || [];

      await updateDoc(orderRef, {
        orderStatus: 'Assorted',
        selectedVariants,
        stampCertificateCost: Number(formData.stampCertificateCost) || 0,
        requestHuidbyOperator: true,
        updatedAt: new Date(),
      });

      setRefreshTrigger((prev) => prev + 1);
      alert('Operator has been requested.');
      setAssortDrawerOpen(false);
    } catch (err) {
      console.error('Error requesting operator:', err);
      alert(`Failed to request operator: ${err.message}`);
    } finally {
      setPendingAction(null);
    }
  }, [assortOrder, sellerId, formData]);

  const handleReject = useCallback(async (firestoreId) => {
    if (pendingAction) return;

    const confirmed = window.confirm('Are you sure you want to reject this request?');
    if (!confirmed) return;

    setPendingAction('reject');

    try {
      const orderRef = doc(db, 'orderList', firestoreId);
      const orderSnap = await getDoc(orderRef);

      if (!orderSnap.exists()) {
        alert('Order not found.');
        setOrders((prev) => prev.filter((o) => o.firestoreId !== firestoreId));
        return;
      }

      const data = orderSnap.data();
      if (data.sellerId !== sellerId) {
        alert('Unauthorized reject attempt.');
        return;
      }

      await updateDoc(orderRef, {
        orderStatus: 'Requested',
        updatedAt: new Date(),
      });

      setRefreshTrigger((prev) => prev + 1);
      alert('Request rejected.');
    } catch (err) {
      console.error('Error rejecting order:', err);
      alert(`Failed to reject: ${err.message}`);
    } finally {
      setPendingAction(null);
    }
  }, [sellerId, pendingAction]);

  // Extract variants safely from an order
  const getVariants = (order) => {
    if (Array.isArray(order.selectedVariants) && order.selectedVariants.length > 0) {
      return order.selectedVariants.map((v) => ({
        size: v.size || 'Standard',
        quantity: v.quantity ?? v.set ?? v.qty ?? 1,
      }));
    }
    if (Array.isArray(order.lotDetails) && order.lotDetails.length > 0) {
      return order.lotDetails.map((l) => ({
        size: l.size || 'Standard',
        quantity: l.set ?? l.quantity ?? l.qty ?? 1,
        avgNetWt: l.avgNetWt,
        avgGrossWt: l.avgGrossWt,
      }));
    }
    if (Array.isArray(order.variants) && order.variants.length > 0) {
      return order.variants.map((v) => ({
        size: v.size || 'Standard',
        quantity: v.quantity ?? v.set ?? v.qty ?? 1,
        avgNetWt: v.avgNetWt,
        avgGrossWt: v.avgGrossWt,
      }));
    }
    if (order.size) {
      return [{ size: order.size, quantity: order.quantity ?? order.set ?? order.qty ?? 1 }];
    }
    return [{ size: 'Standard', quantity: order.quantity ?? order.set ?? order.qty ?? 1 }];
  };

  const getNeteWt = (order, size) => {
    const num = (v) => parseFloat(v) || 0;
    const lotDetail = order.lotDetails?.find((lot) => lot.size === size);
    if (lotDetail && lotDetail.avgNetWt) return num(lotDetail.avgNetWt).toFixed(3);
    if (lotDetail && lotDetail.netWt) return num(lotDetail.netWt).toFixed(3);
    const variant = order.selectedVariants?.find((v) => v.size === size) || order.variants?.find((v) => v.size === size);
    if (variant && variant.avgNetWt) return num(variant.avgNetWt).toFixed(3);
    if (variant && variant.netWt) return num(variant.netWt).toFixed(3);
    if (order.netWt) return num(order.netWt).toFixed(3);
    if (order.avgNetWt) return num(order.avgNetWt).toFixed(3);
    return '0.000';
  };

  const filteredOrders = useMemo(() => {
    if (!selectedCategory || !selectedSource) return orders;
    return orders.filter(
      (order) =>
        order.category?.trim() === selectedCategory.trim() &&
        order.productSource?.trim() === selectedSource.trim()
    );
  }, [orders, selectedCategory, selectedSource]);
  const isLoading = loading;
  if (isLoading) {
    return (
      <>
        <PageHeader title="Ready Services Order Workflow" />
        <div className="workflow-container">
          <div className="loading-skeleton">
            <div className="skeleton-line skeleton-title" />
            <div className="skeleton-grid">
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton-card">
                  <div className="skeleton-image" />
                  <div className="skeleton-text" />
                  <div className="skeleton-text short" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <PageHeader title="Ready Services Order Workflow" />
        <div className="workflow-container">
          <div className="error-state">
            <p className="error-message">{error}</p>
            <Button variant="primary" onClick={fetchOrders}>Retry</Button>
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <PageHeader title="Ready Services Order Workflow" />
     <div className="workflow-container">
        {categories.length === 0 ? (
          <div className="empty-state">
            <p>No categories available for your segment.</p>
          </div>
        ) : (
          <>
            <div className="filters">
              <div className="filter-group">
                <h3>Category</h3>
                <div className="horizontal-scroll-container">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      className={`category-button ${selectedCategory === cat ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedCategory(cat);
                        const firstSource = sourcesByCategory[cat]?.[0] ?? '';
                        setSelectedSource(firstSource);
                      }}
                      type="button"
                    >
                      {cat}
                      {categoryCounts[cat] !== undefined && (
                        <span className="count-badge">
                          {categoryCounts[cat]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="filter-group">
                <h3>Subcategory</h3>
                <div className="horizontal-scroll-container">
                  {sourcesByCategory[selectedCategory]?.map((sub) => (
                    <button
                      key={sub}
                      className={`category-button ${selectedSource === sub ? 'active' : ''}`}
                      onClick={() => setSelectedSource(sub)}
                      type="button"
                    >
                      {sub}
                      {subcategoryCounts[sub] !== undefined && (
                        <span className="count-badge">
                          {subcategoryCounts[sub]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <p className="results-count">
              {filteredOrders.length} request{filteredOrders.length !== 1 ? 's' : ''} found
            </p>

            <div className="orders-grid">
              {filteredOrders.length === 0 ? (
                <div className="no-results">
                  <p>No matching requests.</p>
                </div>
              ) : (
                filteredOrders.map((order) => {
                  const imageUrl = order.images?.[0]?.url?.trim();
                  const orderVariants = getVariants(order);

                  return (
                    <div key={order.firestoreId} className="order-card">
                      <div className="order-image" >
                        {imageUrl ? (
                          <img src={imageUrl} alt={order.productName} />
                        ) : (
                          <div className="image-placeholder">No Image</div>
                        )}
                      </div>

                      <div className="order-info">
                        <div className="order-header">
                          <h5>{order.category}/{order.productName}</h5>
                          <span className={`status-badge status-${order.orderStatus?.toLowerCase()}`}>
                            {order.orderStatus}
                          </span>
                        </div>
                        <p className="order-spec">
                          {order.specification}/{order.styleType}
                        </p>
                        <p>Design: {order.designCode}</p>
                        {orderVariants.length > 0 && (
                          <p className="spec-label">
                            {orderVariants.map((variant, index) => (
                              <span key={variant.size}>
                                {variant.size} x {variant.quantity} ~{getNeteWt(order, variant.size)}g
                                {index < orderVariants.length - 1 ? ', ' : ''}
                              </span>
                            ))}
                          </p>
                        )}
                        {order.createdAt && (
                          <p className="order-date">
                            {new Date(order.createdAt.seconds * 1000 || order.createdAt).toLocaleDateString()}
                          </p>
                        )}

                        <div className="action-btn">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleReject(order.firestoreId)}
                            disabled={pendingAction !== null}
                          >
                            {pendingAction === 'reject' ? 'Rejecting...' : 'Reject'}
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleAssortClick(order)}
                            disabled={pendingAction !== null}
                          >
                            {pendingAction === 'assort' ? 'Assorting...' : 'Assort'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
      <AssortDrawer
        isOpen={assortDrawerOpen}
        onClose={() => setAssortDrawerOpen(false)}
        order={assortOrder}
        pendingAction={pendingAction}
        formData={formData}
        onFormChange={handleFormChange}
        onConfirm={handleConfirmAssort}
        onRequestOperator={handleRequestOperator}
      />
    </>
  );
};

export default Assortment;
